import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Wallet, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from './ui';
import axios from 'axios';

function formatCredits(n) {
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatGbp(n) {
  return `£${Number(n).toFixed(2)}`;
}

function SpendBar({ value, max }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="w-full bg-app-panel-muted rounded-full h-1.5 mt-1">
      <div
        className="bg-accent-500 h-1.5 rounded-full transition-[width] duration-150"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function OrgBillingPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const load = async (m) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/v1/org/billing', { params: { month: m } });
      setData(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403) {
        setError('Org usage breakdown is only available on TEAM or ENTERPRISE plans.');
      } else {
        setError(detail || 'Failed to load org billing data.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(month); }, [month]);

  const maxCredits = data?.users?.length
    ? Math.max(...data.users.map(u => u.total_credits_spent), 1)
    : 1;

  return (
    <div className="min-h-dvh bg-app-bg py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border border-app-border shadow-sm p-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-semibold text-app-text flex items-center gap-2.5">
              <Users className="w-5 h-5 text-accent-600" strokeWidth={2} />
              Team usage
            </h1>
            <div className="flex items-center gap-2">
              <input
                type="month"
                value={month}
                onChange={e => setMonth(e.target.value)}
                className="text-sm border border-app-border-strong rounded-md px-2.5 py-1.5 text-app-text focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => load(month)}
                title="Refresh"
                aria-label="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-[3px] border-accent-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {error && !loading && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {data && !loading && !data.wallet && (
            <div className="text-center py-12">
              <Users className="w-10 h-10 text-app-text-subtle mx-auto mb-3" strokeWidth={1.5} />
              <h2 className="text-base font-semibold text-app-text mb-1">No team wallet</h2>
              <p className="text-sm text-app-text-muted mb-6 max-w-sm mx-auto">
                Team usage breakdowns are available on TEAM and ENTERPRISE plans.
              </p>
              <Button variant="secondary" size="md" onClick={() => navigate('/billing')}>
                Top up personal wallet
              </Button>
            </div>
          )}

          {data && !loading && data.wallet && (
            <>
              {/* Wallet summary */}
              {data.wallet && (
                <div className="grid grid-cols-3 gap-3 mb-8">
                  <div className="bg-accent-50 border border-accent-100 rounded-lg p-4">
                    <div className="text-xs text-accent-700 uppercase tracking-wide mb-1 font-medium">Available</div>
                    <div className="text-xl font-semibold text-accent-900 tabular-nums">
                      {formatCredits(data.wallet.credit_available)}
                      <span className="text-sm font-normal text-accent-600 ml-1">cr</span>
                    </div>
                  </div>
                  <div className="bg-app-panel-muted border border-app-border rounded-lg p-4">
                    <div className="text-xs text-app-text-subtle uppercase tracking-wide mb-1 font-medium">Reserved</div>
                    <div className="text-xl font-semibold text-app-text tabular-nums">
                      {formatCredits(data.wallet.credit_reserved)}
                      <span className="text-sm font-normal text-app-text-muted ml-1">cr</span>
                    </div>
                  </div>
                  <div className="bg-app-panel-muted border border-app-border rounded-lg p-4">
                    <div className="text-xs text-app-text-subtle uppercase tracking-wide mb-1 font-medium">Month spend</div>
                    <div className="text-xl font-semibold text-app-text tabular-nums">
                      {formatGbp(data.total_gbp_spent)}
                    </div>
                  </div>
                </div>
              )}

              {/* Subscription info */}
              {data.subscription && (
                <div className="flex items-center justify-between bg-app-panel-muted border border-app-border rounded-lg px-4 py-3 mb-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-app-text-subtle" />
                    <span className="font-medium text-app-text">
                      {data.subscription.tier}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      data.subscription.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {data.subscription.status}
                    </span>
                  </div>
                  <div className="text-app-text-muted tabular-nums">
                    {data.subscription.seat_count} seat{data.subscription.seat_count !== 1 ? 's' : ''}
                    {data.subscription.period_end && (
                      <span className="ml-3">
                        Renews {new Date(data.subscription.period_end).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Per-user table */}
              <h2 className="text-xs font-semibold text-app-text-subtle uppercase tracking-wide mb-3">
                Usage by member — {data.billing_month}
              </h2>

              {!data.users || data.users.length === 0 ? (
                <div className="text-center py-8 text-app-text-subtle text-sm">
                  No usage recorded for this period.
                </div>
              ) : (
                <div className="divide-y divide-app-border">
                  {data.users.map((u) => (
                    <div key={u.user_id} className="py-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-mono text-app-text-muted truncate max-w-xs">
                          {u.user_id}
                        </span>
                        <div className="text-right ml-4 flex-shrink-0">
                          <span className="text-sm font-semibold text-app-text tabular-nums">
                            {formatCredits(u.total_credits_spent)} cr
                          </span>
                          <span className="text-xs text-app-text-subtle ml-2 tabular-nums">
                            {formatGbp(u.total_gbp)}
                          </span>
                        </div>
                      </div>
                      <SpendBar value={u.total_credits_spent} max={maxCredits} />
                      <div className="flex gap-4 mt-1.5 text-xs text-app-text-subtle tabular-nums">
                        <span>AI: {formatCredits(u.breakdown.model)}</span>
                        <span>Tools: {formatCredits(u.breakdown.tools)}</span>
                        <span>Runtime: {formatCredits(u.breakdown.runtime)}</span>
                        {u.breakdown.storage > 0 && (
                          <span>Storage: {formatCredits(u.breakdown.storage)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-app-border flex justify-between text-sm text-app-text-muted">
                <span>Total this month</span>
                <span className="font-semibold text-app-text tabular-nums">
                  {formatCredits(data.total_credits_spent)} credits &middot; {formatGbp(data.total_gbp_spent)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
